import { deleteCookie, getCookie, setCookie } from 'cookies-next';
import { type GetServerSidePropsContext } from 'next';
import { type NextRequest, type NextResponse } from 'next/server';

import { IS_PRODUCTION } from '@/shared/constants/env';

export type CookieOptions = {
  expires?: Date;
  maxAge?: number;
  httpOnly?: boolean;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  path?: string;
};

export type ServerContext = NextRequest | GetServerSidePropsContext['req'];

/**
 * 쿠키 관리를 위한 클래스
 * Composition 패턴을 사용하여 인스턴스를 생성하고 사용
 */
export class CookieManager {
  private readonly cookieName: string;
  private readonly defaultOptions: CookieOptions;

  constructor(cookieName: string, options?: CookieOptions) {
    this.cookieName = cookieName;
    this.defaultOptions = {
      httpOnly: false,
      secure: IS_PRODUCTION,
      sameSite: 'lax',
      path: '/',
      ...options,
    };
  }

  /**
   * 쿠키 설정
   */
  set(
    value: string,
    context?: ServerContext | NextResponse,
  ): void | NextResponse {
    if (!context) {
      // Client-side
      setCookie(this.cookieName, value, this.defaultOptions);
      return;
    }

    if ('cookies' in context && typeof context.cookies.set === 'function') {
      // NextResponse (middleware)
      const response = context as NextResponse;
      response.cookies.set(this.cookieName, value, this.defaultOptions);
      return response;
    } else {
      // GetServerSidePropsContext['req'] or NextRequest
      if ('cookies' in context && typeof context.cookies.get === 'function') {
        // NextRequest - cannot set cookies directly
        throw new Error(
          'Cannot set cookies on NextRequest. Use NextResponse instead.',
        );
      }
      // GetServerSidePropsContext['req']
      setCookie(this.cookieName, value, {
        req: context as any,
        ...this.defaultOptions,
      });
    }
  }

  /**
   * 쿠키 조회
   */
  get(context?: ServerContext): string | undefined {
    if (!context) {
      // Client-side
      return getCookie(this.cookieName);
    }

    if ('cookies' in context && typeof context.cookies.get === 'function') {
      // NextRequest (middleware)
      return (context as NextRequest).cookies.get(this.cookieName)?.value;
    } else if ('cookies' in context && context.cookies) {
      // GetServerSidePropsContext['req']
      return (context.cookies as any)[this.cookieName];
    }

    return undefined;
  }

  /**
   * 쿠키 삭제
   */
  delete(context?: ServerContext | NextResponse): void | NextResponse {
    if (!context) {
      // Client-side
      deleteCookie(this.cookieName, this.defaultOptions);
      return;
    }

    if ('cookies' in context && typeof context.cookies.delete === 'function') {
      // NextResponse (middleware)
      const response = context as NextResponse;
      response.cookies.delete(this.cookieName);
      return response;
    } else {
      // GetServerSidePropsContext['req'] or NextRequest
      if ('cookies' in context && typeof context.cookies.get === 'function') {
        // NextRequest - cannot delete cookies directly
        throw new Error(
          'Cannot delete cookies on NextRequest. Use NextResponse instead.',
        );
      }
      // GetServerSidePropsContext['req']
      deleteCookie(this.cookieName, {
        req: context as any,
        ...this.defaultOptions,
      });
    }
  }

  /**
   * 쿠키 존재 여부 확인
   */
  has(context?: ServerContext): boolean {
    return this.get(context) !== undefined;
  }
}
